
import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    ActivityIndicator,
} from 'react-native';
import Colors from '../../constant/Colors';
import { getCachedUrlContent } from '../../util/commonFuncs';

// Module-level in-memory cache: storage path / url -> resolved download URL.
// It survives component remounts (FlatList recycling, category switches), so an
// image that has already been resolved once renders instantly on the next mount
// instead of going blank and re-resolving. This is what removes the flicker /
// "reloading" effect when scrolling or switching categories.
const urlMemCache = new Map();

const resolveInitialUrl = (source) => {
    if (source && source.uri) {
        if (source.uri.startsWith('http') || source.uri.startsWith('file:///')) {
            return source.uri;
        }
        const cached = urlMemCache.get(source.uri);
        if (cached) return cached;
    }
    return '';
};

const AsyncImage = props => {
    const {
        source,
        item,
        style,
        hideLoading,
        lazy,
    } = props;

    // Depend on the primitive uri / updatedAt, not the freshly-built source/item
    // objects, so we don't re-fetch on every parent re-render.
    const uri = source && source.uri ? source.uri : '';
    const updatedAt = item && item.updatedAt ? item.updatedAt : undefined;

    const [url, setUrl] = useState(() => resolveInitialUrl(source));
    const [shouldLoad, setShouldLoad] = useState(() => !lazy || resolveInitialUrl(source) !== '');
    const placeholderRef = useRef(null);

    // Lazy loading: only start resolving when the placeholder scrolls near the
    // viewport. rootMargin pre-loads 400px ahead so images are ready before they
    // become visible (less pop-in on fast scroll).
    useEffect(() => {
        if (!lazy) return;
        if (shouldLoad) return;
        if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
            setShouldLoad(true);
            return;
        }
        const node = placeholderRef.current;
        if (!node) {
            setShouldLoad(true);
            return;
        }
        const observer = new window.IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    setShouldLoad(true);
                    observer.disconnect();
                    break;
                }
            }
        }, { rootMargin: '400px 0px' });
        observer.observe(node);
        return () => observer.disconnect();
    }, [lazy, shouldLoad]);

    useEffect(() => {
        if (!shouldLoad) return;

        let cancelled = false;

        const load = async () => {
            if (!uri) {
                setUrl('');
                return;
            }

            if (uri.startsWith('http') || uri.startsWith('file:///')) {
                setUrl(uri);
                return;
            }

            // In-memory hit -> show immediately, no async round trip.
            const mem = urlMemCache.get(uri);
            if (mem) {
                setUrl(mem);
                return;
            }

            if (uri[0] === '/') {
                const cachedObj = await getCachedUrlContent(uri, 43800, item);

                if (!cancelled && cachedObj && cachedObj.parsedUrl) {
                    urlMemCache.set(uri, cachedObj.parsedUrl);
                    setUrl(cachedObj.parsedUrl);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uri, updatedAt, shouldLoad]);

    // Extract numeric width/height from style for the img tag
    const imgWidth = style && typeof style.width === 'number' ? style.width : undefined;
    const imgHeight = style && typeof style.height === 'number' ? style.height : undefined;

    return (
        <>
            {url !== '' ?
                <img
                    decoding="async"
                    src={url}
                    width={imgWidth}
                    height={imgHeight}
                    style={{
                        ...(style && style.width !== undefined && { width: typeof style.width === 'number' ? style.width + 'px' : style.width }),
                        ...(style && style.height !== undefined && { height: typeof style.height === 'number' ? style.height + 'px' : style.height }),
                        ...(style && style.borderRadius !== undefined && { borderRadius: typeof style.borderRadius === 'number' ? style.borderRadius + 'px' : style.borderRadius }),
                        objectFit: 'cover',
                    }}
                />
                :
                <View ref={placeholderRef} style={[{
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                }, style]}>
                    {(!hideLoading && shouldLoad) ? <ActivityIndicator color={Colors.primaryColor} size={"large"} /> : null}
                </View>
            }
        </>
    );
};

export default AsyncImage;
